import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Divider,
  Drawer,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import StatusBadge from './StatusBadge';

const JsonBlock = ({ title, value }) => (
  <Stack spacing={1}>
    <Typography variant="subtitle2">{title}</Typography>
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        borderRadius: 2,
        bgcolor: 'background.default',
        overflow: 'auto',
        fontSize: 12,
      }}
    >
      {JSON.stringify(value, null, 2)}
    </Box>
  </Stack>
);

const LearningRunDetailDrawer = ({ open, run, onClose, loading = false, error = '' }) => {
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', md: 560 }, p: 3 }}>
        {loading ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={28} />
            <Skeleton variant="rounded" height={80} />
            <Skeleton variant="rounded" height={160} />
          </Stack>
        ) : error ? (
          <Typography variant="body2" color="error">{error}</Typography>
        ) : !run ? (
          <Typography variant="body2" color="text.secondary">No run selected.</Typography>
        ) : (
          <Stack spacing={2} divider={<Divider flexItem />}>
            <Box>
              <Typography variant="h6">Learning Run #{run.id}</Typography>
              <Typography variant="body2" color="text.secondary">
                {run.symbol} • {new Date(run.created_at).toLocaleString()}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <StatusBadge value={run.status} />
              <StatusBadge value={run.validation?.validation_state || 'not_applicable'} />
              <StatusBadge value={run.ready_to_learn ? 'passed' : 'not_applicable'} variant="outlined" />
              <Typography variant="caption" color="text.secondary">Sample {run.sample_size}</Typography>
              <Typography variant="caption" color="text.secondary">Duration {run.duration_ms ?? 'N/A'} ms</Typography>
            </Stack>

            <Box>
              <Typography variant="subtitle2">Reason</Typography>
              <Typography variant="body2">{run.reason_code}</Typography>
              {!!Object.keys(run.reason_meta || {}).length && (
                <Typography variant="caption" color="text.secondary">
                  {JSON.stringify(run.reason_meta)}
                </Typography>
              )}
            </Box>

            <JsonBlock title="Input Snapshot" value={run.input_snapshot} />
            <JsonBlock title="Output" value={run.output?.proposal_json ? run.output : { ...run.output, note: 'proposal_json is null' }} />
            <JsonBlock title="Validation" value={run.validation} />
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};

LearningRunDetailDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  run: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

export default LearningRunDetailDrawer;
